import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Upload, FileText, Search, Sparkles, Loader2, Trash2, ArrowLeft,
  Download, Database, RefreshCw, ExternalLink, FileType,
  ShieldCheck, Table2, Wand2, Home, Settings as SettingsIcon,
  ClipboardList, MessageSquare, Library, PenTool, Crown, Trophy, FileCode2,
  ListChecks, Network,
} from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { thesisApi } from '@/api/thesisApi';
import { ThemeToggle } from '@/components/ThemeToggle';
import PlanBadge from '@/components/PlanBadge';
import UserMenu from '@/components/UserMenu';
import TrialBanner from '@/components/TrialBanner';
import WritingStylePanel from '@/components/WritingStylePanel';
import ComplianceCheckDialog from '@/components/ComplianceCheckDialog';
import EvidenceDialog from '@/components/EvidenceDialog';
import AppFooter from '@/components/AppFooter';
import RUOBadge from '@/components/RUOBadge';
import LegalDisclaimer from '@/components/LegalDisclaimer';
import InkPhDLogo from '@/components/InkPhDLogo';
import ClaimSafetyDialog from '@/components/ClaimSafetyDialog';
import AITransparencyPanel from '@/components/AITransparencyPanel';
import ResearchMemoryPanel from '@/components/ResearchMemoryPanel';
import AICoachCard from '@/components/AICoachCard';
import IntelligencePanel from '@/components/IntelligencePanel';
import DefenseSimulationDialog from '@/components/DefenseSimulationDialog';
import ClaimGroundingDialog from '@/components/ClaimGroundingDialog';
import FindingsTableDialog from '@/components/FindingsTableDialog';
import SelectionToolbar from '@/components/SelectionToolbar';
import PlagiarismDialog from '@/components/PlagiarismDialog';
import GenerateFieldButton from '@/components/GenerateFieldButton';
import LanguagePicker from '@/components/LanguagePicker';
import RetrievalModeToggle from '@/components/RetrievalModeToggle';
import PremiumSectionDialog from '@/components/PremiumSectionDialog';
import PremiumUpgradeDialog from '@/components/PremiumUpgradeDialog';
import AIStructureBuilderDialog from '@/components/AIStructureBuilderDialog';
import ChapterLibraryDialog from '@/components/ChapterLibraryDialog';
import ChaptersListPanel from '@/components/ChaptersListPanel';
import ChapterEditorPanel from '@/components/ChapterEditorPanel';
import ResizableWorkspaceLayout from '@/components/ResizableWorkspaceLayout';
import WaveDDialog from '@/components/WaveDDialog';
import ReadinessDashboardDialog from '@/components/ReadinessDashboardDialog';
import ProjectReadinessOverviewDialog from '@/components/ProjectReadinessOverviewDialog';
import SmartContinueDialog from '@/components/SmartContinueDialog';
import CodeProjectPanel from '@/components/CodeProjectPanel';
import GettingStartedPanel from '@/components/GettingStartedPanel';
import FiguresPanel from '@/components/FiguresPanel';
import SavedTablesPanel from '@/components/SavedTablesPanel';
import DataStatsPanel from '@/components/DataStatsPanel';
import AdvancedStatsPanel from '@/components/AdvancedStatsPanel';
import ManuscriptIntakePanel from '@/components/ManuscriptIntakePanel';
import WritingPlanPanel from '@/components/WritingPlanPanel';
import ConsensusMeterPanel from '@/components/ConsensusMeterPanel';
import CitationNetworkPanel from '@/components/CitationNetworkPanel';
import ReferenceImportPanel from '@/components/ReferenceImportPanel';
import ZoteroPanel from '@/components/ZoteroPanel';
import LiteratureSearchPanel from '@/components/LiteratureSearchPanel';
import ScreeningPanel from '@/components/ScreeningPanel';
import FrontMatterPanel from '@/components/FrontMatterPanel';

const PROJECT_TYPE_LABEL = {
  dissertation: 'dissertation',
  research_article: 'research article',
  systematic_review: 'systematic review',
  meta_analysis: 'meta-analysis',
  clinical_protocol: 'clinical protocol',
  grant_proposal: 'grant proposal',
  book: 'book',
  case_report: 'case report',
  conference_abstract: 'conference abstract',
  editorial: 'editorial',
};

export default function ThesisWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('workspace');
  const [aiDisclosure, setAiDisclosure] = useState(true);

  // Compliance + Evidence dialog state
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [complianceChapter, setComplianceChapter] = useState(null);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceQuery, setEvidenceQuery] = useState('');

  // PubMed state
  const [pubmedQuery, setPubmedQuery] = useState('');
  const [pubmedResults, setPubmedResults] = useState([]);
  const [pubmedSearching, setPubmedSearching] = useState(false);
  // Wave B v2 — PubMed v2 advanced filters
  const [pubmedFilters, setPubmedFilters] = useState({
    date_from: '',
    date_to: '',
    study_types: [],
    free_full_text: false,
    mesh_terms: [],
    sort: 'relevance',
    max_results: 25,
  });
  const [meshSuggestQuery, setMeshSuggestQuery] = useState('');
  const [meshSuggestions, setMeshSuggestions] = useState([]);
  const [pubmedIngestingIds, setPubmedIngestingIds] = useState(new Set());

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Chapter state
  const [chapterDialogOpen, setChapterDialogOpen] = useState(false);
  const [chapterForm, setChapterForm] = useState({ title: '', prompt: '', n_chunks: 10, min_words: 3000 });
  const [generating, setGenerating] = useState(false);
  const [openChapterId, setOpenChapterId] = useState(null);
  const [editorFocusKey, setEditorFocusKey] = useState(0);

  // Claim safety state
  const [safetyDialogOpen, setSafetyDialogOpen] = useState(false);
  const [safetyResult, setSafetyResult] = useState(null);
  const [safetyChecking, setSafetyChecking] = useState(false);
  const [pendingGenerate, setPendingGenerate] = useState(false);

  // Findings Table dialog
  const [findingsTableOpen, setFindingsTableOpen] = useState(false);

  // Structural parts included in exports + refresh signal for saved tables
  const [exportParts, setExportParts] = useState({
    titlePage: true, toc: true, abbreviations: true, declaration: true,
    figureList: true, tableList: true,
  });
  const [tablesRefresh, setTablesRefresh] = useState(0);
  const [screeningRecords, setScreeningRecords] = useState([]);
  const [cslQuery, setCslQuery] = useState('');
  const [cslStyles, setCslStyles] = useState([]);
  const [fullTextBusy, setFullTextBusy] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [fullTextProgress, setFullTextProgress] = useState('');

  const handleFullTextFetch = async () => {
    setFullTextBusy(true);
    setFullTextProgress('');
    let fetched = 0;
    let processed = 0;
    try {
      for (let round = 0; round < 8; round += 1) {
        const res = await thesisApi.fetchFullTexts(user.id, projectId, 5);
        fetched += res.fetched;
        processed += res.processed;
        setFullTextProgress(`${fetched}/${processed}`);
        if (!res.processed || !res.remaining) break;
      }
      await loadAll();
      toast.success(processed
        ? `Full text retrieved for ${fetched} of ${processed} sources (the rest are not open access)`
        : 'Every source already has its full text');
    } catch (e) {
      toast.error('Full-text fetch failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setFullTextBusy(false);
      setFullTextProgress('');
    }
  };
  const [assetLabels, setAssetLabels] = useState({ labels: {}, figure_word: 'Figure', table_word: 'Table' });

  // Continue writing (Jenni-style)
  const [continuing, setContinuing] = useState(false);

  // Wave B v2 — Plagiarism / originality dialog
  const [plagiarismOpen, setPlagiarismOpen] = useState(false);

  // Wave B v2 — Whole-article generation (adaptive workflow)
  const [generatingArticle, setGeneratingArticle] = useState(false);

  // Wave B v2 — citation style for exports
  const [citationStyle, setCitationStyle] = useState('vancouver');
  const [bibPreview, setBibPreview] = useState(null);
  const [bibLoading, setBibLoading] = useState(false);

  // Wave B v2 — per-generation language override (default: project language)
  const [aiLanguage, setAiLanguage] = useState(project?.language || 'en');
  useEffect(() => { setAiLanguage(project?.language || 'en'); }, [project?.language]);

  // Wave C+ — retrieval mode (dense | hybrid | bm25). Hybrid is the default.
  const [retrievalMode, setRetrievalMode] = useState('hybrid');

  // Admin / Premium feature flags (loaded once on mount).
  const [featureFlags, setFeatureFlags] = useState({ premium_section_writer: false, premium: null });
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [premiumUpgradeOpen, setPremiumUpgradeOpen] = useState(false);

  // Sprint 1 — AI Structure Builder + Chapter-specific Library
  const [structureBuilderOpen, setStructureBuilderOpen] = useState(false);
  const [chapterLibraryFor, setChapterLibraryFor] = useState(null); // chapter object
  // Sprint 3 + 4 — Defense Simulation + Claim Grounding
  const [defenseOpen, setDefenseOpen] = useState(false);
  const [groundingOpen, setGroundingOpen] = useState(false);
  const [waveDOpen, setWaveDOpen] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [projectReadinessOpen, setProjectReadinessOpen] = useState(false);
  // Sprint 7 — Smart Continue dialog (anchored to a paragraph or cursor)
  const [smartContinueState, setSmartContinueState] = useState({
    open: false, precedingText: '', followingText: '', anchorLabel: '',
  });
  // Drag-and-drop chapter reorder (Sprint 5 continued)
  const [draggedChapterId, setDraggedChapterId] = useState(null);
  const [dragOverChapterId, setDragOverChapterId] = useState(null);
  const [dragOverMode, setDragOverMode] = useState(null);  // 'above' | 'below' | 'into'
  useEffect(() => {
    if (!user?.id) return;
    thesisApi.getFeatureFlags(user.id).then(setFeatureFlags).catch(() => {});
  }, [user?.id]);

  // Wave B v2 — chapter editor mode toggle
  const [editorMode, setEditorMode] = useState(false);

  useEffect(() => {
    if (!authStorage.isAuthorAuthenticated()) {
      navigate('/thesis/login');
      return;
    }
    setUser(authStorage.getAuthorUser());
  }, [navigate]);

  const loadAll = useCallback(async () => {
    if (!user?.id || !projectId) return;
    setLoading(true);
    try {
      const [p, d, ch] = await Promise.all([
        thesisApi.getProject(user.id, projectId),
        thesisApi.listDocuments(user.id, projectId),
        thesisApi.listChapters(user.id, projectId),
      ]);
      setProject(p);
      setDocs(d.documents || []);
      setChapters(ch.chapters || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) toast.error('Project not found');
      else if (status === 403) toast.error('Access denied');
      else toast.error('Error: ' + (e.response?.data?.detail || e.message));
      navigate('/thesis');
    } finally {
      setLoading(false);
    }
  }, [user, projectId, navigate]);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  // Figure/table labels for cross-references — refreshed whenever assets change
  useEffect(() => {
    if (!user?.id || !projectId) return;
    thesisApi.getAssetLabels(user.id, projectId)
      .then(setAssetLabels)
      .catch(() => {});
  }, [user, projectId, tablesRefresh, chapters.length]);

  // Sprint 6 — normalize chapter order ONCE on initial mount. Fixes legacy
  // projects where every chapter has order=0 (visually looks like duplicates
  // / nested items). Idempotent on the backend. Also backfills citation
  // bibliographic metadata so the Vancouver "Sources" footer renders properly.
  useEffect(() => {
    if (!user?.id || !projectId) return;
    Promise.all([
      thesisApi.normalizeChapterOrder(user.id, projectId).catch(() => null),
      thesisApi.enrichChapterCitations(user.id, projectId).catch(() => null),
    ]).then((results) => {
      const anyUpdated = results.some((r) => r?.updates > 0 || r?.enriched_chapters > 0);
      if (anyUpdated) loadAll();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, projectId]);

  // Auto-refresh chapters that are still running (premium background tasks)
  // so the user sees the result as soon as the engine finishes.
  //
  // Sprint 6 fix (2026-02-15) — cap polling per chapter at 30 minutes.
  // Sprint 7 UX fix (2026-02-16) — instead of calling loadAll() (which reloads
  // the project, documents, chapters, and triggers a full re-render of the
  // editor, AI Coach, Intelligence Panel, etc.), we now ONLY refetch the
  // running chapters and patch them in place. The editor stays mounted, the
  // page does not "flicker", and the user can keep reading while a background
  // generation completes.
  useEffect(() => {
    if (!user || !chapters?.length) return;
    const now = Date.now();
    const POLL_BUDGET_MS = 30 * 60 * 1000;
    const runningIds = chapters.filter((c) => {
      if (c.premium_status !== 'running') return false;
      const ts = c.updated_at ? Date.parse(c.updated_at) : now;
      return Number.isFinite(ts) && (now - ts) < POLL_BUDGET_MS;
    }).map((c) => c.id);
    if (runningIds.length === 0) return;

    const id = setInterval(async () => {
      try {
        const results = await Promise.all(
          runningIds.map((cid) => thesisApi.getChapter(user.id, cid).catch(() => null))
        );
        setChapters((prev) => prev.map((c) => {
          const fresh = results.find((r) => r?.id === c.id);
          return fresh || c;
        }));
      } catch { /* network blip — keep going */ }
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, chapters]);

  // Non-destructive in-place patch used by ChapterLiveEditor's auto-save so
  // we don't trigger a full project reload (which would unmount the editor
  // mid-LLM call and lose the in-flight ghost suggestion).
  const patchChapterMarkdown = useCallback((chapterId, newMarkdown) => {
    setChapters((prev) => prev.map((c) => (c.id === chapterId ? { ...c, markdown: newMarkdown } : c)));
  }, []);

  // Auto-poll while any document is still being indexed in background
  useEffect(() => {
    const anyPending = docs.some((d) => !d.indexed && !d.index_error);
    if (!anyPending) return;
    const t = setInterval(() => {
      thesisApi.listDocuments(user.id, projectId)
        .then((data) => setDocs(data.documents || []))
        .catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [docs, user, projectId]);

  // ---- DOCUMENT UPLOAD ----
  const handleFileUpload = async (e, sourceType = 'upload') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await thesisApi.uploadDocument(user.id, projectId, file, null, sourceType);
      const kind = sourceType === 'style_reference' ? 'style reference' : 'document';
      toast.success(`"${file.name}" uploaded as ${kind}`);
      loadAll();
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ---- TABLE GENERATION (feature F) ----
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableTopic, setTableTopic] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  const [generatedTable, setGeneratedTable] = useState(null);

  const handleGenerateTable = async () => {
    if (!tableTopic.trim()) { toast.error('Provide a topic'); return; }
    setTableLoading(true);
    try {
      const res = await thesisApi.generateTable(user.id, projectId, tableTopic, 8, aiLanguage);
      setGeneratedTable(res);
      toast.success('Table generated');
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTableLoading(false);
    }
  };

  // ---- VERSION HISTORY (feature H) ----
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsOpenFor, setVersionsOpenFor] = useState(null);

  const loadVersions = async (chapterId) => {
    setVersionsLoading(true);
    setVersionsOpenFor(chapterId);
    try {
      const data = await thesisApi.listVersions(user.id, chapterId);
      setVersions(data.versions || []);
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRestoreVersion = async (chapterId, versionId) => {
    if (!window.confirm('Restore this version? The current text will be saved in history.')) return;
    try {
      await thesisApi.restoreVersion(user.id, chapterId, versionId);
      toast.success('Version restored');
      setVersionsOpenFor(null);
      loadAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- AI REVIEWER MODE ----
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewOpenFor, setReviewOpenFor] = useState(null);
  const [applyingIssue, setApplyingIssue] = useState(null);

  const handleRunReview = async (chapterId) => {
    setReviewLoading(true);
    setReviewOpenFor(chapterId);
    setReview(null);
    try {
      const data = await thesisApi.reviewChapter(user.id, chapterId);
      // Attach chapter_id so the panel can scope display to the current chapter
      setReview({ ...data, chapter_id: chapterId });
      toast.success(`Reviewer score: ${data.overall_score}/10`);
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
      setReviewOpenFor(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleApplySuggestion = async (issueIndex) => {
    if (!review?.id || !reviewOpenFor) return;
    setApplyingIssue(issueIndex);
    try {
      await thesisApi.applySuggestion(user.id, reviewOpenFor, review.id, issueIndex);
      toast.success('Suggestion applied. Section updated.');
      // mark applied locally so UI re-renders without closing the dialog
      setReview((r) => {
        if (!r) return r;
        const issues = [...(r.issues || [])];
        if (issues[issueIndex]) issues[issueIndex] = { ...issues[issueIndex], applied: true };
        return { ...r, issues };
      });
      // Refresh chapters list (and selected chapter) in the background
      loadAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setApplyingIssue(null);
    }
  };

  const handleDeleteDoc = async (docId, title) => {
    if (!window.confirm(`Remove "${title}"?`)) return;
    try {
      await thesisApi.deleteDocument(user.id, projectId, docId);
      toast.success('Document removed');
      loadAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- DRAG-AND-DROP CHAPTER REORDER (Sprint 5 continued) ----
  // HTML5 native drag-and-drop. Three drop modes:
  //   - "above"/"below": same-parent reorder via /chapters/reorder bulk update
  //   - "into": become a sub-section of the target via /chapters/{id}/hierarchy
  const onChapterDragStart = useCallback((e, chId) => {
    setDraggedChapterId(chId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', chId); } catch { /* ignore safari quirks */ }
  }, []);

  const onChapterDragOver = useCallback((e, targetId, depth) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!draggedChapterId || draggedChapterId === targetId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    // Three zones — top 30%: above; bottom 30%: below; middle 40%: into (sub-section)
    // Only allow "into" when target depth allows nesting (depth<2)
    let mode;
    if (y < h * 0.3) mode = 'above';
    else if (y > h * 0.7) mode = 'below';
    else mode = (depth >= 2) ? 'below' : 'into';
    setDragOverChapterId(targetId);
    setDragOverMode(mode);
  }, [draggedChapterId]);

  const onChapterDragLeave = useCallback(() => {
    // Soft clear — only clear if we leave without entering another row
    setDragOverChapterId(null);
    setDragOverMode(null);
  }, []);

  const onChapterDragEnd = useCallback(() => {
    setDraggedChapterId(null);
    setDragOverChapterId(null);
    setDragOverMode(null);
  }, []);

  const onChapterDrop = useCallback(async (e, targetId, targetDepth, targetParentId) => {
    e.preventDefault();
    const sourceId = draggedChapterId || (e.dataTransfer && e.dataTransfer.getData('text/plain'));
    setDraggedChapterId(null);
    const mode = dragOverMode;
    setDragOverChapterId(null);
    setDragOverMode(null);
    if (!sourceId || sourceId === targetId) return;

    try {
      if (mode === 'into') {
        // Nest under target
        await thesisApi.setChapterHierarchy(user.id, sourceId, targetId);
        toast.success('Nested as sub-section');
      } else {
        // Same-parent reorder. First, ensure source's parent_id matches target's parent_id.
        const source = chapters.find(c => c.id === sourceId);
        const targetParent = targetParentId || null;
        if (source && (source.parent_id || null) !== targetParent) {
          await thesisApi.setChapterHierarchy(user.id, sourceId, targetParent);
        }
        // Recompute order list among siblings of `targetParent`
        const siblings = chapters
          .filter(c => (c.parent_id || null) === targetParent && c.id !== sourceId)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        const targetIdx = siblings.findIndex(c => c.id === targetId);
        const insertIdx = mode === 'above' ? targetIdx : targetIdx + 1;
        const newOrder = [...siblings.slice(0, insertIdx), { id: sourceId }, ...siblings.slice(insertIdx)];
        const orders = newOrder.map((c, i) => ({ id: c.id, order: i }));
        await thesisApi.reorderChapters(user.id, projectId, orders);
        toast.success('Chapters reordered');
      }
      loadAll();
    } catch (err) {
      toast.error(`Reorder failed: ${err?.response?.data?.detail || err.message}`);
    }
  }, [draggedChapterId, dragOverMode, chapters, user, projectId, loadAll]);

  // ---- PUBMED ----
  const handlePubmedSearch = async () => {
    if (!pubmedQuery.trim() && (!pubmedFilters.mesh_terms || pubmedFilters.mesh_terms.length === 0)) {
      toast.error('Provide a query or at least one MeSH term');
      return;
    }
    setPubmedSearching(true);
    setPubmedResults([]);
    try {
      const data = await thesisApi.pubmedSearchV2(user.id, {
        query: pubmedQuery,
        max_results: pubmedFilters.max_results,
        date_from: pubmedFilters.date_from || null,
        date_to: pubmedFilters.date_to || null,
        study_types: pubmedFilters.study_types,
        free_full_text: pubmedFilters.free_full_text,
        mesh_terms: pubmedFilters.mesh_terms,
        sort: pubmedFilters.sort,
      });
      setPubmedResults(data.articles || []);
      toast.success(`${data.count} articles found`);
    } catch (e) {
      toast.error('PubMed error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPubmedSearching(false);
    }
  };

  const handleResetPubmedSearch = () => {
    setPubmedQuery('');
    setPubmedResults([]);
    setMeshSuggestions([]);
    setMeshSuggestQuery('');
    setPubmedFilters({
      date_from: '', date_to: '', study_types: [], free_full_text: false,
      mesh_terms: [], sort: 'relevance', max_results: 25,
    });
    toast.success('Search cleared');
  };

  const handleMeshSuggest = async (q) => {
    setMeshSuggestQuery(q);
    if (!q || q.length < 3) { setMeshSuggestions([]); return; }
    try {
      const data = await thesisApi.pubmedMeshSuggest(user.id, q);
      setMeshSuggestions(data.suggestions || []);
    } catch {
      setMeshSuggestions([]);
    }
  };

  const addMeshTerm = (term) => {
    if (!term || pubmedFilters.mesh_terms.includes(term)) return;
    setPubmedFilters({ ...pubmedFilters, mesh_terms: [...pubmedFilters.mesh_terms, term] });
    setMeshSuggestQuery('');
    setMeshSuggestions([]);
  };

  const removeMeshTerm = (term) => {
    setPubmedFilters({ ...pubmedFilters, mesh_terms: pubmedFilters.mesh_terms.filter(t => t !== term) });
  };

  const toggleStudyType = (st) => {
    const cur = pubmedFilters.study_types;
    setPubmedFilters({
      ...pubmedFilters,
      study_types: cur.includes(st) ? cur.filter(t => t !== st) : [...cur, st],
    });
  };

  const handleIngestPubmed = async (pmid) => {
    setPubmedIngestingIds((prev) => new Set(prev).add(pmid));
    try {
      const res = await thesisApi.pubmedIngest(user.id, projectId, [pmid]);
      if (res.ingested > 0) {
        toast.success('Article added to the project');
        loadAll();
      } else {
        toast.info('Article already added');
      }
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPubmedIngestingIds((prev) => {
        const s = new Set(prev);
        s.delete(pmid);
        return s;
      });
    }
  };

  const handleIngestAll = async () => {
    const pmids = pubmedResults.map((a) => a.pmid).filter(Boolean);
    if (!pmids.length) return;
    setPubmedSearching(true);
    try {
      const res = await thesisApi.pubmedIngest(user.id, projectId, pmids);
      toast.success(`Added: ${res.ingested}, skipped: ${res.skipped_duplicates}`);
      loadAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPubmedSearching(false);
    }
  };

  // ---- CHAPTER GENERATION ----
  // If chapterForm.chapter_id is set, the request will update an EXISTING
  // chapter (chapter-specific library retrieval, Sprint 1 — Step 3).
  // Otherwise it creates a brand-new chapter using the full project library.
  const _doGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const ch = await thesisApi.generateChapter(
        user.id, projectId,
        chapterForm.title, chapterForm.prompt,
        chapterForm.n_chunks, aiLanguage,
        chapterForm.min_words, retrievalMode,
        chapterForm.chapter_id || null,
        chapterForm.allow_pool_fallback !== false,
        { fastMode: !!chapterForm.fast_mode },
      );
      if (ch?.premium_status === 'running') {
        const baseMin = chapterForm.fast_mode
          ? Math.max(1, Math.round((chapterForm.min_words / 2) / 1200))
          : Math.max(2, Math.round(chapterForm.min_words / 800));
        const baseMax = chapterForm.fast_mode
          ? Math.max(2, Math.round((chapterForm.min_words / 2) / 800))
          : Math.max(3, Math.round(chapterForm.min_words / 600));
        toast.success(
          `"${ch.title}" generation started (~${baseMin}-${baseMax} min${chapterForm.fast_mode ? ', Fast mode' : ''}). The card will refresh when ready.`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Section "${ch.title}" generated (${ch.citations?.length || 0} citations)`);
      }
      setChapterDialogOpen(false);
      setChapterForm({ title: '', prompt: '', n_chunks: 10, min_words: 3000, fast_mode: false });
      setOpenChapterId(ch.id);
      setTab('chapters');
      loadAll();
    } catch (e) {
      toast.error('Could not start generation: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  }, [user, projectId, chapterForm, aiLanguage, retrievalMode, loadAll]);

  const handleGenerateChapter = async () => {
    if (!chapterForm.title.trim()) {
      toast.error('Section title is required');
      return;
    }
    if (docs.length === 0) {
      toast.error('Upload at least one document or ingest PubMed articles');
      return;
    }
    // Step 1: pre-generation claim safety check on title + prompt
    const probe = `${chapterForm.title}\n\n${chapterForm.prompt || ''}`.trim();
    setSafetyChecking(true);
    try {
      const result = await thesisApi.safetyCheckText(user.id, probe, aiLanguage);
      setSafetyChecking(false);
      if (result.classification === 'safe') {
        // No warning needed — generate immediately
        _doGenerate();
      } else {
        // Surface a warning dialog, let the user decide
        setSafetyResult(result);
        setPendingGenerate(true);
        setSafetyDialogOpen(true);
      }
    } catch (e) {
      // If the safety check itself fails, continue (don't punish the user for a transient failure)
      setSafetyChecking(false);
      _doGenerate();
    }
  };

  const handleSafetyContinue = () => {
    setSafetyDialogOpen(false);
    if (pendingGenerate) {
      setPendingGenerate(false);
      _doGenerate();
    }
  };

  const handleSafetyCancel = () => {
    setSafetyDialogOpen(false);
    setPendingGenerate(false);
  };

  // ---- CONTINUE WRITING (Jenni-style) ----
  const handleContinueWriting = async () => {
    if (!openChapter || !openChapter.markdown || openChapter.markdown.length < 60) {
      toast.error('Chapter is too short to continue. Generate a chapter first.');
      return;
    }
    setContinuing(true);
    try {
      const data = await thesisApi.continueWriting(
        user.id, projectId, openChapter.markdown, aiLanguage, 2,
      );
      if (!data.continuation || !data.continuation.trim()) {
        toast.warning('AI returned an empty continuation. Try again.');
        return;
      }
      // Append in-paragraph: join with a single space so the continuation
      // flows into the SAME paragraph the author was finishing (no extra blank
      // line). The backend prompt already forbids new paragraphs/headings.
      const trimmed = openChapter.markdown.trimEnd();
      const cont = data.continuation.trim();
      const sep = trimmed.endsWith('.') || trimmed.endsWith('!') || trimmed.endsWith('?')
        ? ' '   // sentence ended cleanly → just a space
        : '';   // mid-sentence → glue directly
      const newMarkdown = `${trimmed}${sep}${cont}\n`;
      await thesisApi.updateChapter(user.id, openChapter.id, { markdown: newMarkdown });
      toast.success(`Added ${data.continuation.split(/\s+/).length} words (${data.chunks_used} sources used)`);
      loadAll();
    } catch (e) {
      toast.error('Continue failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setContinuing(false);
    }
  };

  // ---- WHOLE-ARTICLE GENERATION (adaptive workflow for non-thesis types) ----
  const isSectionBased = ['dissertation', 'book'].includes(project?.project_type || 'dissertation');
  const handleGenerateWholeArticle = async () => {
    if (docs.length === 0) {
      toast.error('Add at least one document or PubMed article first.');
      return;
    }
    setGeneratingArticle(true);
    try {
      const data = await thesisApi.generateWholeArticle(user.id, projectId, {
        title: project.title,
        research_question: project.research_question || '',
        description: project.description || '',
        language: aiLanguage,
        retrieval_mode: retrievalMode,
      });
      if (data.premium_status === 'running') {
        toast.success(
          `Whole-article generation started (~3-8 min). The card will refresh when ready.`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Article generated (${data.citations_used?.length || 0} citations, ${data.chunks_used} sources)`);
      }
      if (data.saved_chapter_id) {
        setOpenChapterId(data.saved_chapter_id);
        setTab('chapters');
      }
      loadAll();
    } catch (e) {
      toast.error('Could not start generation: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGeneratingArticle(false);
    }
  };

  // ---- SELECTION-TOOLBAR text replace ----
  const handleSelectionReplace = async (original, replacement) => {
    if (!openChapter) return;
    const md = openChapter.markdown || '';
    if (!md.includes(original)) {
      // Selection may span paragraphs / whitespace differences — try a normalized search
      const norm = (s) => s.replace(/\s+/g, ' ').trim();
      const normOrig = norm(original);
      const idx = norm(md).indexOf(normOrig);
      if (idx < 0) {
        toast.warning('Could not locate the original passage in the saved markdown.');
        return;
      }
    }
    const newMd = md.replace(original, replacement);
    try {
      await thesisApi.updateChapter(user.id, openChapter.id, { markdown: newMd });
      loadAll();
    } catch (e) {
      toast.error('Update failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- MULTI-STYLE CITATION EXPORT ----
  const handleBuildBibliography = async (style) => {
    setBibLoading(true);
    setCitationStyle(style);
    try {
      const data = await thesisApi.formatCitations(user.id, projectId, style);
      setBibPreview(data);
      if (!data.bibliography) toast.warning('No citations found in your sections yet.');
    } catch (e) {
      toast.error('Format failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBibLoading(false);
    }
  };

  const downloadBibliography = () => {
    if (!bibPreview?.bibliography) return;
    const ext = citationStyle === 'bibtex' ? 'bib' : 'txt';
    const blob = new Blob([bibPreview.bibliography], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bibliography-${citationStyle}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- continue writing handler placeholder (preserve original below) ----
  // (handleContinueWriting moved inline above)
  const _continueDummy = async () => {
    if (!openChapter || !openChapter.markdown || openChapter.markdown.length < 60) {
      toast.error('Chapter is too short to continue. Generate a chapter first.');
      return;
    }
    setContinuing(true);
    try {
      const lang = project?.language || 'en';
      const data = await thesisApi.continueWriting(
        user.id, projectId, openChapter.markdown, lang, 3,
      );
      if (!data.continuation || !data.continuation.trim()) {
        toast.warning('AI returned an empty continuation. Try again.');
        return;
      }
      // Append + persist
      const newMarkdown = openChapter.markdown.trimEnd() + '\n\n' + data.continuation.trim() + '\n';
      await thesisApi.updateChapter(user.id, openChapter.id, { markdown: newMarkdown });
      toast.success(`Added ${data.continuation.split(/\s+/).length} words (${data.chunks_used} sources used)`);
      loadAll();
    } catch (e) {
      toast.error('Continue failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setContinuing(false);
    }
  };

  const handleDeleteChapter = async (chapterId, title) => {
    if (!window.confirm(`Delete the section "${title}"?`)) return;
    try {
      await thesisApi.deleteChapter(user.id, chapterId);
      toast.success('Section deleted');
      if (openChapterId === chapterId) setOpenChapterId(null);
      loadAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- EXPORTS ----
  const exportAuthor = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : (user?.username || 'Author');

  if (loading || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const openChapter = chapters.find((c) => c.id === openChapterId) || chapters[0];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-stone-50 via-background to-background dark:from-slate-950 dark:via-background text-foreground thesis-brand">
      <LegalDisclaimer variant="banner" lang="en" />
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" asChild data-testid="back-to-projects" className="shrink-0">
              <Link to="/thesis"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <Link to="/thesis" className="hidden sm:flex items-center gap-2 shrink-0" data-testid="thesis-logo-mini">
              <InkPhDLogo size="sm" />
              <RUOBadge className="hidden md:inline-flex" />
            </Link>
            <span className="text-muted-foreground hidden md:inline">/</span>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-lg font-semibold leading-tight break-words line-clamp-2 sm:line-clamp-1 lg:line-clamp-none"
                title={project.title} data-testid="thesis-workspace-title">{project.title}</h1>
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                {project.document_count || 0} docs · {project.chapter_count || 0} sections
                <span className="hidden sm:inline"> · {project.vector_stats?.total_chunks || 0} evidence chunks</span>
                {project.primary_guideline_id && <> · <span className="text-[#1d4ed8] dark:text-[#7aa9ff] uppercase tracking-wider">{project.primary_guideline_id}</span></>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setFindingsTableOpen(true)} data-testid="findings-table-btn" className="hidden sm:inline-flex">
              <Table2 className="w-3.5 h-3.5 mr-1.5" /> Findings table
            </Button>
            <Button variant="outline" size="icon" onClick={() => setFindingsTableOpen(true)} data-testid="findings-table-btn-mobile" className="sm:hidden h-8 w-8">
              <Table2 className="w-3.5 h-3.5" />
            </Button>
            <div className="hidden md:flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">AI output</span>
              <LanguagePicker value={aiLanguage} onChange={setAiLanguage} size="sm" testid="header-language-picker" />
            </div>
            <Button variant="ghost" size="sm" asChild data-testid="workspace-landing-link" className="shrink-0">
              <Link to="/" title="Open the public site — your session stays active">
                <Home className="w-4 h-4" />
              </Link>
            </Button>

            <PlanBadge compact />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="w-full min-w-0 max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <TrialBanner urgentOnly />
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="w-full max-w-full grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-12 h-auto gap-1 p-1.5 bg-muted/40 dark:bg-white/[0.02] rounded-xl sticky top-[68px] z-20 backdrop-blur supports-[backdrop-filter]:bg-muted/60" data-testid="workspace-tabs">
            {[
              ['workspace', Home, 'Workspace', ''],
              ['plan', ListChecks, 'Plan', ''],
              ['research', MessageSquare, 'Research', ''],
              ['guidelines', ClipboardList, 'Guidelines', ''],
              ['documents', Library, 'Library', String(docs.length)],
              ['pubmed', Search, 'PubMed', ''],
              ['insights', Network, 'Insights', ''],
              ['tables', Table2, 'Figures', ''],
              ['chapters', PenTool, 'Writing', String(chapters.length)],
              ['code', FileCode2, 'Python', ''],
              ['export', Download, 'Export', ''],
              ['settings', SettingsIcon, 'Settings', ''],
            ].map(([value, Icon, label, count]) => (
              <TabsTrigger key={value} value={value} data-testid={`tab-${value}`}
                title={count ? `${label} (${count})` : label}
                className="min-w-0 justify-center px-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <Icon className="w-4 h-4 mr-1.5 shrink-0" />
                <span className="truncate">{label}</span>
                {count && (
                  <span className="ml-1 shrink-0 text-[10px] tabular-nums text-muted-foreground">
                    {count}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="min-w-0">
            {/* WORKSPACE DASHBOARD */}
            <TabsContent value="workspace" className="space-y-4 mt-0" data-testid="workspace-dashboard">
              <GettingStartedPanel userId={user.id} projectId={projectId}
                onGoToTab={(t) => setTab(t)} />
              <ManuscriptIntakePanel userId={user.id} projectId={projectId}
                onImported={() => loadAll()} onOpenPlan={() => setTab('plan')} />
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif text-2xl" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                    {project.title}
                  </CardTitle>
                  <CardDescription>
                    {project.description || project.research_question || 'No description yet — add one in Research Question.'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <DashStat label="Documents" value={project.document_count || 0} icon={Library} />
                    <DashStat label="Writing sections" value={project.chapter_count || 0} icon={PenTool} />
                    <DashStat label="Evidence chunks" value={project.vector_stats?.total_chunks || 0} icon={Database} />
                  </div>

                  {/* Adaptive workflow: whole-article generator for non-section types */}
                  {!isSectionBased && (
                    <div className="mt-5 p-4 rounded-lg border border-[#1d4ed8]/30 bg-gradient-to-br from-[#1d4ed8]/5 to-transparent dark:from-[#1d4ed8]/10">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-widest text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold mb-1">
                            One-shot article generator
                          </div>
                          <div className="font-serif text-base font-semibold mb-1" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                            Generate the complete {PROJECT_TYPE_LABEL[project.project_type] || 'manuscript'} in one go
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Uses all your project's evidence library to produce a fully-structured manuscript with title, abstract,
                            canonical sections, and Manubot citations. Prefer full control? Open the Writing tab and use
                            <span className="font-semibold"> AI Structure</span> to build the sections one by one instead.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <LanguagePicker value={aiLanguage} onChange={setAiLanguage} testid="article-language-picker" />
                          <Button onClick={handleGenerateWholeArticle} disabled={generatingArticle || docs.length === 0}
                                  className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white"
                                  data-testid="generate-whole-article-btn">
                            {generatingArticle
                              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                              : <><Sparkles className="w-4 h-4 mr-2" /> Generate article</>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {project.primary_guideline_id && (
                    <div className="mt-5 p-4 rounded-lg border border-[#1d4ed8]/20 bg-[#1d4ed8]/5 dark:bg-[#1d4ed8]/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold mb-1">
                        Primary reporting guideline
                      </div>
                      <div className="font-serif text-lg font-semibold" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                        {project.primary_guideline_id.toUpperCase()}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* WRITING PLAN */}
            <TabsContent value="plan" className="space-y-4 mt-0" data-testid="plan-tab-content">
              <WritingPlanPanel userId={user.id} projectId={projectId}
                onGoToChapter={(chapterId) => { setOpenChapterId(chapterId); setTab('chapters'); }} />
            </TabsContent>

            {/* INSIGHTS — consensus meter + citation network */}
            <TabsContent value="insights" className="space-y-4 mt-0" data-testid="insights-tab-content">
              <ConsensusMeterPanel userId={user.id} projectId={projectId} />
              <CitationNetworkPanel userId={user.id} projectId={projectId} />
            </TabsContent>


            {/* RESEARCH QUESTION */}
            <TabsContent value="research" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                    Research question & project description
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Research question</div>
                      <GenerateFieldButton
                        userId={user.id} projectId={projectId} field="research_question"
                        language={project.language || 'en'}
                        onResult={async (value) => {
                          try {
                            await thesisApi.updateProject(user.id, projectId, { research_question: value });
                            toast.success('Research question updated');
                            loadAll();
                          } catch (e) { toast.error('Update failed: ' + (e.response?.data?.detail || e.message)); }
                        }}
                      />
                    </div>
                    <p className="leading-relaxed">{project.research_question || <span className="text-muted-foreground italic">Not provided. Use "Generate" to draft one from your project description and evidence.</span>}</p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Project description</div>
                    <p className="leading-relaxed whitespace-pre-wrap">{project.description || <span className="text-muted-foreground italic">Not provided</span>}</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                    <FieldGenCard userId={user.id} projectId={projectId} field="title" language={project.language || 'en'} label="Title" />
                    <FieldGenCard userId={user.id} projectId={projectId} field="abstract" language={project.language || 'en'} label="Abstract" />
                    <FieldGenCard userId={user.id} projectId={projectId} field="keywords" language={project.language || 'en'} label="Keywords" />
                    <FieldGenCard userId={user.id} projectId={projectId} field="hypothesis" language={project.language || 'en'} label="Hypothesis" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* GUIDELINES */}
            <TabsContent value="guidelines" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                    EQUATOR reporting guideline
                  </CardTitle>
                  <CardDescription>Your project's primary guideline drives compliance scoring and auto-fix suggestions.</CardDescription>
                </CardHeader>
                <CardContent>
                  {project.primary_guideline_id ? (
                    <div className="text-sm">
                      <div className="font-serif text-2xl text-[#1d4ed8] dark:text-[#7aa9ff] mb-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                        {project.primary_guideline_id.toUpperCase()}
                      </div>
                      <p className="text-muted-foreground mb-3">
                        Run a Compliance check from any writing section to see how the draft scores against this guideline.
                      </p>
                      <a href="https://www.equator-network.org/" target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 text-sm text-[#1d4ed8] dark:text-[#7aa9ff] hover:underline">
                        Visit EQUATOR Network <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No primary guideline set. You can pick one in Settings.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* FIGURES, TABLES & DATA */}
            <TabsContent value="tables" className="space-y-4 mt-0">
              <FiguresPanel userId={user.id} projectId={projectId} chapters={chapters}
                refreshKey={tablesRefresh} figureWord={assetLabels.figure_word}
                onInserted={() => loadAll()} onAssetsChanged={() => setTablesRefresh((n) => n + 1)} />
              <SavedTablesPanel userId={user.id} projectId={projectId} chapters={chapters}
                refreshKey={tablesRefresh} tableWord={assetLabels.table_word}
                onInserted={() => loadAll()} onAssetsChanged={() => setTablesRefresh((n) => n + 1)} />
              <DataStatsPanel userId={user.id} projectId={projectId}
                onTableSaved={() => setTablesRefresh((n) => n + 1)} />
              <AdvancedStatsPanel userId={user.id} projectId={projectId}
                refreshKey={tablesRefresh} onAssetsChanged={() => setTablesRefresh((n) => n + 1)}
                onOpenChapter={async (chId) => {
                  await loadAll();
                  setTab('chapters');
                  setEditorMode(false);
                  setOpenChapterId(chId);
                  setEditorFocusKey((n) => n + 1);
                  setTimeout(() => {
                    document.querySelector(`[data-testid="chapter-editor-scroll-anchor"]`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 350);
                }} />
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                    Evidence tables
                  </CardTitle>
                  <CardDescription>
                    Extract PICO comparison tables across multiple sources in your library. Population, intervention,
                    comparator, outcome, effect size, p-value — all grounded, never unresolved.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => setFindingsTableOpen(true)} className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white">
                    <Table2 className="w-4 h-4 mr-2" /> Extract findings table
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SETTINGS */}
            <TabsContent value="settings" className="space-y-4 mt-0">
              <WritingStylePanel userId={user.id} project={project} onUpdated={loadAll} />
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                    Project settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <KV k="Title" v={project.title} />
                  <KV k="Project type" v={project.project_type || 'research_article'} />
                  <KV k="Language" v={(project.language || 'en').toUpperCase()} />
                  <KV k="Primary guideline" v={project.primary_guideline_id || '—'} />
                  <KV k="Field" v={project.field || '—'} />
                  <KV k="Specialty" v={project.specialty || '—'} />
                  <KV k="Created" v={project.created_at?.slice(0, 10) || '—'} />
                </CardContent>
              </Card>

              {/* Sprint 5 — Research Memory */}
              <ResearchMemoryPanel userId={user.id} projectId={projectId} />

              <FrontMatterPanel userId={user.id} projectId={projectId} />
            </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-4">
            <Card data-testid="fulltext-fetch-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex flex-wrap items-center gap-2">
                  <Download className="w-5 h-5 text-[#1d4ed8]" /> Open-access full texts
                </CardTitle>
                <CardDescription>
                  Records added from a search hold only the abstract. This pulls the full text for
                  every one of them that is legally open access (Europe PMC · Unpaywall · OpenAlex ·
                  arXiv) and re-indexes it, so your writing is grounded in whole papers.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <Button onClick={handleFullTextFetch} disabled={fullTextBusy}
                  data-testid="fulltext-fetch-btn">
                  {fullTextBusy
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching… {fullTextProgress}</>
                    : <><Download className="w-4 h-4 mr-2" /> Fetch full texts</>}
                </Button>
                <span className="text-xs text-muted-foreground" data-testid="fulltext-count">
                  {docs.filter((d) => d.fulltext).length} of {docs.length} sources have full text
                </span>
                <div className="flex-1" />
                <Button variant="outline" disabled={reindexing}
                  onClick={async () => {
                    if (!window.confirm('Rebuild the vector index for every source in this project? Nothing is deleted; it can take a few minutes.')) return;
                    setReindexing(true);
                    try {
                      const res = await thesisApi.reindexProject(user.id, projectId);
                      toast.success(`Library re-indexed — ${res.indexed_documents ?? res.documents ?? ''} sources, ${res.total_chunks ?? res.chunks ?? ''} chunks`.replace(/\s+,/g, ','));
                      loadAll();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Re-index failed');
                    } finally { setReindexing(false); }
                  }}
                  title="Re-chunk and re-embed every source — use after a failed upload or when retrieval looks wrong"
                  data-testid="reindex-library-btn">
                  {reindexing
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-indexing…</>
                    : <><RefreshCw className="w-4 h-4 mr-2" /> Re-index library</>}
                </Button>
              </CardContent>
            </Card>
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-teal-700 dark:text-teal-400" />
                    Evidence document
                  </CardTitle>
                  <CardDescription>PDF / DOCX / MD / TXT — used as a source of facts and citations</CardDescription>
                </CardHeader>
                <CardContent>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.docx,.md,.txt"
                      onChange={(e) => handleFileUpload(e, 'upload')} disabled={uploading}
                      className="hidden" data-testid="upload-doc-input" />
                    <Button asChild disabled={uploading} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
                      <span>
                        {uploading
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                          : <><Upload className="w-4 h-4 mr-2" />Upload evidence document</>}
                      </span>
                    </Button>
                  </label>
                </CardContent>
              </Card>

              <Card className="border-amber-300/50 dark:border-amber-700/50 bg-amber-50/30 dark:bg-amber-950/20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                    Style reference <Badge variant="outline" className="text-xs">A</Badge>
                  </CardTitle>
                  <CardDescription>Reference manuscript — AI adapts structural and rhetorical patterns without reproducing content</CardDescription>
                </CardHeader>
                <CardContent>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.docx,.md,.txt"
                      onChange={(e) => handleFileUpload(e, 'style_reference')} disabled={uploading}
                      className="hidden" data-testid="upload-style-input" />
                    <Button asChild disabled={uploading} variant="outline" className="w-full border-amber-600 text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-950">
                      <span>
                        {uploading
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                          : <><Upload className="w-4 h-4 mr-2" />Upload style reference</>}
                      </span>
                    </Button>
                  </label>
                </CardContent>
              </Card>
            </div>

            <ReferenceImportPanel userId={user.id} projectId={projectId} />
            <ZoteroPanel userId={user.id} projectId={projectId} onSynced={() => loadAll()} />

            {docs.length === 0 ? (
              <Card className="text-center py-12 border-dashed">
                <CardContent>
                  <Database className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No indexed documents yet</p>
                </CardContent>
              </Card>
            ) : (              <div className="grid sm:grid-cols-2 gap-4" data-testid="docs-list">
                {docs.map((d) => (
                  <Card key={d.id} data-testid={`doc-card-${d.id}`}
                    className={!d.indexed && !d.index_error ? 'border-amber-300 dark:border-amber-700' : ''}>
                    <CardContent className="pt-5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-medium leading-snug line-clamp-2">{d.title}</h3>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(d.id, d.title)}
                          data-testid={`doc-delete-${d.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs items-center">
                        <Badge variant={d.source_type === 'style_reference' ? 'default' : 'secondary'}
                          className={d.source_type === 'style_reference' ? 'bg-amber-600' : ''}>
                          {d.source_type === 'style_reference' ? 'style reference' : d.source_type}
                        </Badge>
                        {!d.indexed && !d.index_error && (
                          <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                            <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />Indexing...
                          </Badge>
                        )}
                        {d.index_error && (
                          <Badge variant="destructive" title={d.index_error}>⚠ Error</Badge>
                        )}
                        {d.pmid && (
                          <a href={`https://pubmed.ncbi.nlm.nih.gov/${d.pmid}/`} target="_blank" rel="noopener noreferrer">
                            <Badge variant="outline" className="hover:bg-accent">PMID: {d.pmid}</Badge>
                          </a>
                        )}
                        {d.doi && (
                          <a href={`https://doi.org/${d.doi}`} target="_blank" rel="noopener noreferrer">
                            <Badge variant="outline" className="hover:bg-accent">DOI</Badge>
                          </a>
                        )}
                        {d.indexed && <Badge variant="outline">{d.chunk_count} chunks</Badge>}
                      </div>
                      {d.index_error && (
                        <p className="text-xs text-destructive mt-2">{d.index_error}</p>
                      )}
                      {d.authors?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                          {d.authors.slice(0, 3).join(', ')}{d.authors.length > 3 && '...'}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PUBMED TAB */}
          <TabsContent value="pubmed" className="space-y-4">
            <LiteratureSearchPanel userId={user.id} projectId={projectId}
              onIngested={() => loadAll()}
              onSendToScreening={(records) => {
                setScreeningRecords(records);
                toast.success(`${records.length} records ready for PRISMA screening below`);
              }} />
            <ScreeningPanel userId={user.id} projectId={projectId}
              pendingRecords={screeningRecords}
              onConsumedRecords={() => setScreeningRecords([])}
              onAssetsChanged={() => setTablesRefresh((n) => n + 1)}
              onIngested={() => loadAll()} />
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-3">
                <div>
                  <CardTitle className="font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>PubMed search</CardTitle>
                  <CardDescription>Find and add medical literature directly from NCBI — with MeSH, study-type, and date filters.</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={handleResetPubmedSearch} data-testid="pubmed-reset-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Clear & restart
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Query row */}
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. breast cancer screening dense breasts MRI"
                    value={pubmedQuery}
                    onChange={(e) => setPubmedQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePubmedSearch()}
                    data-testid="pubmed-query-input"
                  />
                  <Button onClick={handlePubmedSearch} disabled={pubmedSearching}
                    className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white"
                    data-testid="pubmed-search-btn">
                    {pubmedSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />} Search
                  </Button>
                </div>

                {/* MeSH terms */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap">MeSH terms</span>
                    <div className="relative flex-1">
                      <Input
                        placeholder="e.g. Pancreatitis, Acute Necrotizing"
                        value={meshSuggestQuery}
                        onChange={(e) => handleMeshSuggest(e.target.value)}
                        data-testid="pubmed-mesh-input"
                        className="h-8 text-xs"
                      />
                      {meshSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 z-20 bg-popover border border-border rounded-lg shadow-lg max-h-56 overflow-auto" data-testid="mesh-suggestions">
                          {meshSuggestions.map((s, i) => (
                            <button key={s.term || `mesh-${i}`} type="button"
                              className="w-full text-left px-3 py-2 hover:bg-accent text-xs"
                              onClick={() => addMeshTerm(s.term)}
                              data-testid={`mesh-suggestion-${i}`}>
                              <div className="font-medium">{s.term}</div>
                              {s.scope_note && <div className="text-[11px] text-muted-foreground line-clamp-1">{s.scope_note}</div>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {pubmedFilters.mesh_terms.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="pubmed-mesh-chips">
                      {pubmedFilters.mesh_terms.map((m) => (
                        <Badge key={m} variant="secondary" className="text-[11px] cursor-pointer hover:bg-rose-100 dark:hover:bg-rose-950/40"
                          onClick={() => removeMeshTerm(m)}>
                          {m} <span className="ml-1.5 opacity-60">×</span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Filters grid */}
                <div className="grid sm:grid-cols-4 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">From</div>
                    <Input type="date" value={pubmedFilters.date_from}
                      onChange={(e) => setPubmedFilters({ ...pubmedFilters, date_from: e.target.value.replace(/-/g, '/') })}
                      className="h-8 text-xs" data-testid="pubmed-date-from"/>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">To</div>
                    <Input type="date" value={pubmedFilters.date_to}
                      onChange={(e) => setPubmedFilters({ ...pubmedFilters, date_to: e.target.value.replace(/-/g, '/') })}
                      className="h-8 text-xs" data-testid="pubmed-date-to"/>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Sort</div>
                    <select value={pubmedFilters.sort}
                      onChange={(e) => setPubmedFilters({ ...pubmedFilters, sort: e.target.value })}
                      className="h-8 w-full text-xs rounded-md border border-input bg-background px-2"
                      data-testid="pubmed-sort">
                      <option value="relevance">Relevance</option>
                      <option value="pub_date">Newest first</option>
                      <option value="first_author">First author</option>
                    </select>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Max</div>
                    <select value={pubmedFilters.max_results}
                      onChange={(e) => setPubmedFilters({ ...pubmedFilters, max_results: parseInt(e.target.value, 10) })}
                      className="h-8 w-full text-xs rounded-md border border-input bg-background px-2"
                      data-testid="pubmed-max-results">
                      {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                {/* Study types + free full text */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mr-1">Study type</span>
                  {['Randomized Controlled Trial', 'Meta-Analysis', 'Systematic Review', 'Clinical Trial', 'Observational Study', 'Case Reports', 'Review'].map((st) => (
                    <button key={st} type="button" onClick={() => toggleStudyType(st)}
                      className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                        pubmedFilters.study_types.includes(st)
                          ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                          : 'bg-background text-muted-foreground border-border hover:border-[#1d4ed8]/40'
                      }`}
                      data-testid={`pubmed-study-${st.replace(/\s+/g,'-').toLowerCase()}`}
                    >{st}</button>
                  ))}
                  <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={pubmedFilters.free_full_text}
                      onChange={(e) => setPubmedFilters({ ...pubmedFilters, free_full_text: e.target.checked })}
                      className="accent-[#1d4ed8]"
                      data-testid="pubmed-fft"
                    /> Free full text only
                  </label>
                </div>

                {pubmedResults.length > 0 && (
                  <div className="flex justify-between items-center text-sm pt-2 border-t border-border">
                    <span className="text-muted-foreground">{pubmedResults.length} results</span>
                    <Button size="sm" variant="outline" onClick={handleIngestAll} disabled={pubmedSearching}
                      data-testid="ingest-all-btn">
                      <Database className="w-3 h-3 mr-1" /> Add all to library
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {pubmedResults.length > 0 && (
              <div className="space-y-3" data-testid="pubmed-results">
                {pubmedResults.map((a) => {
                  const isIngesting = pubmedIngestingIds.has(a.pmid);
                  const alreadyAdded = docs.some((d) => d.pmid === a.pmid);
                  return (
                    <Card key={a.pmid} data-testid={`pubmed-result-${a.pmid}`}>
                      <CardContent className="pt-5">
                        <h3 className="font-medium mb-1">{a.title}</h3>
                        <p className="text-xs text-muted-foreground mb-2">
                          {a.authors?.slice(0, 5).join(', ')}{a.authors?.length > 5 && '...'} · {a.journal} · {a.year}
                        </p>
                        {a.abstract && <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{a.abstract}</p>}
                        <div className="flex items-center gap-2 flex-wrap">
                          <a href={`https://pubmed.ncbi.nlm.nih.gov/${a.pmid}/`} target="_blank" rel="noopener noreferrer">
                            <Badge variant="outline" className="hover:bg-accent">
                              PMID: {a.pmid} <ExternalLink className="w-3 h-3 ml-1 inline" />
                            </Badge>
                          </a>
                          {a.doi && (
                            <a href={`https://doi.org/${a.doi}`} target="_blank" rel="noopener noreferrer">
                              <Badge variant="outline" className="hover:bg-accent">DOI</Badge>
                            </a>
                          )}
                          {alreadyAdded ? (
                            <Badge variant="secondary">✓ Added</Badge>
                          ) : (
                            <Button size="sm" variant="default" onClick={() => handleIngestPubmed(a.pmid)}
                              disabled={isIngesting} data-testid={`ingest-pubmed-${a.pmid}`}>
                              {isIngesting
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <><Database className="w-3 h-3 mr-1" />Add</>}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* CHAPTERS TAB */}
          <TabsContent value="chapters" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>Writing sections</CardTitle>
                  <CardDescription>Evidence-grounded section generation from your indexed sources.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Dialog open={tableDialogOpen} onOpenChange={(o) => { setTableDialogOpen(o); if (!o) setGeneratedTable(null); }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" disabled={docs.length === 0} data-testid="generate-table-btn">
                        <FileType className="w-4 h-4 mr-2" /> Generate table
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Generate data table</DialogTitle>
                        <DialogDescription>
                          AI extracts quantitative findings from sources and renders them as a cited markdown table.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <Input value={tableTopic}
                          onChange={(e) => setTableTopic(e.target.value)}
                          placeholder="e.g. Sensitivity of MRI vs mammography"
                          data-testid="table-topic-input" />
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Output language</span>
                          <LanguagePicker value={aiLanguage} onChange={setAiLanguage} testid="table-language-picker" />
                        </div>
                        <Button onClick={handleGenerateTable} disabled={tableLoading} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
                          {tableLoading
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                            : <><Sparkles className="w-4 h-4 mr-2" />Generate</>}
                        </Button>
                        {generatedTable && (
                          <div className="border rounded-md p-3 bg-muted/30 max-h-96 overflow-auto" data-testid="generated-table-result">
                            <div className="text-xs text-muted-foreground mb-2">{generatedTable.chunks_used} sources</div>
                            <article className="prose prose-sm dark:prose-invert max-w-none">
                              <ReactMarkdown components={{
                                p: ({ children }) => <p>{renderCitations(children)}</p>,
                                td: ({ children }) => <td>{renderCitations(children)}</td>,
                              }}>{generatedTable.markdown}</ReactMarkdown>
                            </article>
                            <Button size="sm" variant="ghost" className="mt-2"
                              onClick={() => {
                                navigator.clipboard.writeText(generatedTable.markdown);
                                toast.success('Copied to clipboard');
                              }}>
                              Copy markdown
                            </Button>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={chapterDialogOpen} onOpenChange={setChapterDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="generate-chapter-btn" disabled={docs.length === 0}
                        className="bg-teal-700 hover:bg-teal-800 text-white">
                        <Sparkles className="w-4 h-4 mr-2" /> Generate section
                      </Button>
                    </DialogTrigger>
                  <Button
                    onClick={() => (featureFlags.premium_section_writer
                      ? setPremiumOpen(true) : setPremiumUpgradeOpen(true))}
                    disabled={docs.length === 0 && featureFlags.premium_section_writer}
                    className={`ml-2 text-white ${featureFlags.premium_section_writer
                      ? 'bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-800 hover:to-amber-700'
                      : 'bg-slate-400 hover:bg-slate-500'}`}
                    data-testid="premium-section-btn"
                    title={featureFlags.premium_section_writer
                      ? 'Premium multi-pass section writer — counts as 5 generations per run'
                      : 'Premium RAG Section Writer — available from the Researcher plan'}
                  >
                    <Crown className="w-4 h-4 mr-2" /> Premium
                    {featureFlags.premium?.remaining >= 0 && featureFlags.premium_section_writer && (
                      <span className="ml-1.5 text-[11px] opacity-90"
                        data-testid="premium-remaining-badge">
                        {featureFlags.premium.remaining} left
                      </span>
                    )}
                    {!featureFlags.premium_section_writer && (
                      <span className="ml-1.5 text-[11px] opacity-90"
                        data-testid="premium-locked-badge">Upgrade</span>
                    )}
                  </Button>
                  <Button
                    onClick={() => setStructureBuilderOpen(true)}
                    variant="outline"
                    className="ml-2 border-[#1d4ed8]/40 text-[#1d4ed8] hover:bg-[#1d4ed8]/5 dark:text-[#7aa9ff]"
                    data-testid="ai-structure-builder-btn"
                    title={`AI proposes the canonical section structure for a ${(PROJECT_TYPE_LABEL[project?.project_type] || 'manuscript').toLowerCase()}`}
                  >
                    <Wand2 className="w-4 h-4 mr-2" /> AI Structure
                  </Button>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Generate a new writing section</DialogTitle>
                      <DialogDescription>
                        Section will be generated by GPT-5.2 grounded in the indexed evidence.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Section title *</label>
                        <Input value={chapterForm.title}
                          onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })}
                          placeholder="e.g. Literature review"
                          data-testid="chapter-title-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Additional instructions</label>
                        <Textarea value={chapterForm.prompt}
                          onChange={(e) => setChapterForm({ ...chapterForm, prompt: e.target.value })}
                          placeholder="Focus on sensitivity vs specificity..."
                          rows={3}
                          data-testid="chapter-prompt-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Evidence chunks in context: {chapterForm.n_chunks}</label>
                        <input type="range" min={4} max={20} value={chapterForm.n_chunks}
                          onChange={(e) => setChapterForm({ ...chapterForm, n_chunks: parseInt(e.target.value, 10) })}
                          className="w-full" data-testid="chapter-nchunks-slider" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">
                          Target length: <span className="text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold">{chapterForm.min_words.toLocaleString()} words</span>
                          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold ml-2">
                            {chapterForm.min_words < 1500 ? 'Short' : chapterForm.min_words < 3000 ? 'Medium' : chapterForm.min_words < 5000 ? 'Long' : 'Very long'}
                          </span>
                        </label>
                        <input type="range" min={1000} max={8000} step={500} value={chapterForm.min_words}
                          onChange={(e) => setChapterForm({ ...chapterForm, min_words: parseInt(e.target.value, 10) })}
                          className="w-full" data-testid="chapter-minwords-slider" />
                        <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                          <span>1k</span><span>3k</span><span>5k</span><span>8k</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-2.5 rounded-md bg-muted/40 border border-border">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Output language</div>
                          <div className="text-xs text-muted-foreground">Choose the language the AI will write in</div>
                        </div>
                        <LanguagePicker value={aiLanguage} onChange={setAiLanguage} testid="chapter-language-picker" />
                      </div>
                      <div className="flex items-center justify-between p-2.5 rounded-md bg-muted/40 border border-border">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Retrieval mode</div>
                          <div className="text-xs text-muted-foreground">How evidence chunks are ranked. Hybrid is recommended.</div>
                        </div>
                        <RetrievalModeToggle value={retrievalMode} onChange={setRetrievalMode} testid="chapter-retrieval-toggle" />
                      </div>
                      {/* Sprint 7 polish — Fast Mode toggle */}
                      <div className="flex items-center justify-between p-2.5 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900">
                        <div className="flex-1 min-w-0 pr-3">
                          <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-semibold flex items-center gap-1">
                            ⚡ Fast mode
                          </div>
                          <div className="text-xs text-amber-800 dark:text-amber-200">
                            Halves chunks &amp; target length, swaps to gpt-4o-mini.
                            <span className="font-semibold"> ~30–90 s</span> instead of ~5 min. Lower quality — refine after.
                          </div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!chapterForm.fast_mode}
                          data-testid="chapter-fast-mode-toggle"
                          onClick={() => setChapterForm({ ...chapterForm, fast_mode: !chapterForm.fast_mode })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            chapterForm.fast_mode ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'
                          }`}>
                          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-md transform transition ${
                            chapterForm.fast_mode ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setChapterDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleGenerateChapter} disabled={generating || safetyChecking} data-testid="chapter-submit-btn"
                        className="bg-teal-700 hover:bg-teal-800 text-white">
                        {safetyChecking
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Safety check...</>
                          : generating
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating ({Math.round(chapterForm.min_words / 1000)}-{Math.round(chapterForm.min_words / 600)} min)...</>
                          : <><Sparkles className="w-4 h-4 mr-2" />Generate</>}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                </div>
              </CardHeader>
            </Card>

            {chapters.length === 0 ? (
              <Card className="text-center py-12 border-dashed">
                <CardContent>
                  <Sparkles className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    {docs.length === 0
                      ? 'First add documents or PubMed articles, then generate a section'
                      : 'No sections generated yet'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
              {/* AI Coach card — recommends next actions (Sprint 5+) */}
              <AICoachCard
                userId={user.id}
                projectId={projectId}
                projectType={project?.project_type}
                onJumpToChapter={(chId) => { setOpenChapterId(chId); setEditorMode(false); }}
              />
              {/* Project-wide Readiness Overview trigger */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setProjectReadinessOpen(true)}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950/30"
                  data-testid="open-project-readiness">
                  <Trophy className="w-4 h-4 mr-2" />
                  Project Readiness Overview
                </Button>
              </div>
              <ResizableWorkspaceLayout
                storageKey={`inkphd-workspace-layout-${projectId}`}
                focusKey={editorFocusKey}
                left={(
                  <ChaptersListPanel
                    chapters={chapters}
                    docs={docs}
                    userId={user.id}
                    openChapter={openChapter}
                    draggedChapterId={draggedChapterId}
                    dragOverChapterId={dragOverChapterId}
                    dragOverMode={dragOverMode}
                    onSelectChapter={(chId) => { setOpenChapterId(chId); setEditorMode(false); }}
                    onDeleteChapter={handleDeleteChapter}
                    onChapterDragStart={onChapterDragStart}
                    onChapterDragOver={onChapterDragOver}
                    onChapterDragLeave={onChapterDragLeave}
                    onChapterDragEnd={onChapterDragEnd}
                    onChapterDrop={onChapterDrop}
                    onOpenChapterLibrary={(c) => setChapterLibraryFor(c)}
                    onOpenGenerateDialog={(c) => {
                      setChapterForm({
                        title: c.title,
                        prompt: c.description || '',
                        n_chunks: 12,
                        min_words: 3000,
                        chapter_id: c.id,
                      });
                      setChapterDialogOpen(true);
                    }}
                    onHierarchyChanged={loadAll}
                  />
                )}
                center={(
                  <ChapterEditorPanel
                    openChapter={openChapter}
                    userId={user.id}
                    projectId={projectId}
                    assetLabels={assetLabels}
                    editorMode={editorMode}
                    continuing={continuing}
                    aiLanguage={aiLanguage}
                    onLoadVersions={loadVersions}
                    onOpenEvidence={() => { setEvidenceQuery(''); setEvidenceOpen(true); }}
                    onOpenCompliance={(ch) => { setComplianceChapter(ch); setComplianceOpen(true); }}
                    onContinueWriting={handleContinueWriting}
                    onToggleEditorMode={() => setEditorMode((m) => !m)}
                    onOpenPlagiarism={() => setPlagiarismOpen(true)}
                    onRunReview={handleRunReview}
                    onChapterSaved={patchChapterMarkdown}
                    onCancelGeneration={async (chId) => {
                      try {
                        await thesisApi.cancelChapterGeneration(user.id, chId);
                        toast.success('Generation cancelled');
                        loadAll();
                      } catch (e) {
                        toast.error(`Cancel failed: ${e?.response?.data?.detail || e.message}`);
                      }
                    }}
                    onOpenWaveD={() => setWaveDOpen(true)}
                    onOpenReadiness={() => setReadinessOpen(true)}
                    onSmartContinue={(precedingText, followingText, anchorLabel) =>
                      setSmartContinueState({ open: true, precedingText, followingText, anchorLabel })
                    }
                    onRefreshChapter={async (chId) => {
                      try {
                        const fresh = await thesisApi.getChapter(user.id, chId);
                        setChapters((prev) => prev.map((c) => (c.id === chId ? { ...c, ...fresh } : c)));
                      } catch { /* the next full load will pick it up */ }
                    }}
                  />
                )}
                right={(
                  <div className="space-y-3">
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-950 shadow-sm">
                      <IntelligencePanel
                        userId={user.id}
                        projectId={projectId}
                        chapterId={openChapter?.id}
                        openChapter={openChapter}
                        language={aiLanguage}
                        onOpenEvidence={() => { setEvidenceQuery(''); setEvidenceOpen(true); }}
                        onOpenCompliance={(ch) => { setComplianceChapter(ch); setComplianceOpen(true); }}
                        onOpenReview={(chId) => handleRunReview(chId)}
                        onOpenOriginality={() => setPlagiarismOpen(true)}
                        onOpenDefense={() => setDefenseOpen(true)}
                        onOpenGrounding={() => setGroundingOpen(true)}
                        reviewData={review}
                        reviewLoading={reviewLoading}
                        applyingIssue={applyingIssue}
                        onApplyReviewIssue={handleApplySuggestion}
                        onResetReview={() => { setReviewOpenFor(null); setReview(null); }}
                      />
                    </div>
                    {openChapter && (
                      <details className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3 group">
                        <summary className="text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer flex items-center gap-1">
                          <span className="group-open:rotate-90 transition inline-block">▸</span>
                          AI Transparency snapshot
                        </summary>
                        <div className="mt-2">
                          <AITransparencyPanel
                            userId={user.id}
                            chapter={openChapter}
                            onChapterUpdated={loadAll}
                          />
                        </div>
                      </details>
                    )}
                  </div>
                )}
              />
              </>
            )}
          </TabsContent>

          {/* EXPORT TAB */}
          {/* PYTHON PROJECT → ARTICLE */}
          <TabsContent value="code" className="space-y-4" data-testid="code-tab-content">
            <CodeProjectPanel
              userId={user.id}
              projectId={projectId}
              language={aiLanguage}
              chapters={chapters}
              onInserted={() => { loadAll(); setTab('chapters'); }}
              onOpenChapter={(id) => { setTab('chapters'); setOpenChapterId(id); }}
            />
          </TabsContent>

          <TabsContent value="export" className="space-y-4">
            <LegalDisclaimer variant="modal" lang="en" />
            <Card>
              <CardHeader>
                <CardTitle>Manuscript exports</CardTitle>
                <CardDescription>
                  Download the full manuscript in your preferred format. Citations are preserved as Manubot references (Markdown) or as hyperlinks (HTML / DOCX).
                </CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <ExportCard
                  icon={<FileType className="w-8 h-8" />}
                  title="Manubot Markdown"
                  desc=".md with @doi:/@pmid: syntax"
                  href={thesisApi.exportMarkdownUrl(user.id, projectId, exportAuthor, aiDisclosure)}
                  testid="export-md"
                />
                <ExportCard
                  icon={<FileText className="w-8 h-8" />}
                  title="HTML"
                  desc="Web-ready document"
                  href={thesisApi.exportHtmlUrl(user.id, projectId, exportAuthor, aiDisclosure, exportParts)}
                  testid="export-html"
                />
                <ExportCard
                  icon={<Download className="w-8 h-8" />}
                  title="Word (.docx)"
                  desc="Real tables, embedded figures"
                  href={thesisApi.exportDocxUrl(user.id, projectId, exportAuthor, aiDisclosure, exportParts)}
                  testid="export-docx"
                />
                <ExportCard
                  icon={<FileText className="w-8 h-8" />}
                  title="PDF"
                  desc="Print-ready / submission-ready"
                  href={thesisApi.exportPdfUrl(user.id, projectId, exportAuthor, aiDisclosure, exportParts)}
                  testid="export-pdf"
                />
                <ExportCard
                  icon={<FileType className="w-8 h-8" />}
                  title="LaTeX"
                  desc="main.tex + figures (ZIP)"
                  href={thesisApi.exportLatexUrl(user.id, projectId, exportAuthor)}
                  testid="export-latex"
                />
              </CardContent>
              <div className="border-t border-border px-6 py-4" data-testid="export-structural-parts">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                  Structural document parts
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-xs">
                  {[
                    ['titlePage', 'Title page', 'Formatted cover with university, degree, supervisor'],
                    ['toc', 'Table of contents', 'Auto-built from your section headings'],
                    ['abbreviations', 'List of abbreviations', 'From the front matter list in Settings'],
                    ['declaration', 'Declaration of originality', 'Signed statement required by most faculties'],
                    ['figureList', 'List of figures', 'Every figure with its number and caption'],
                    ['tableList', 'List of tables', 'Every table with its number and caption'],
                  ].map(([key, label, hint]) => (
                    <label key={key} className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={exportParts[key]}
                        onChange={(e) => setExportParts((p) => ({ ...p, [key]: e.target.checked }))}
                        className="mt-0.5 accent-teal-700"
                        data-testid={`export-part-${key}`} />
                      <span>
                        <span className="font-semibold">{label}</span>{' '}
                        <span className="text-muted-foreground">— {hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Fill in the details under <span className="font-semibold">Settings → Front matter</span>.
                  Applies to Word, PDF and HTML exports.
                </p>
              </div>
              <div className="border-t border-border px-6 py-3 bg-amber-50/40 dark:bg-amber-950/20 flex items-start gap-3" data-testid="export-ai-disclosure">
                <label className="flex items-start gap-2 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={aiDisclosure}
                    onChange={(e) => setAiDisclosure(e.target.checked)}
                    className="mt-0.5 accent-teal-700"
                    data-testid="export-ai-disclosure-toggle"
                  />
                  <span className="text-xs leading-relaxed">
                    <span className="font-semibold">Include AI-assistance disclosure + watermark in the export.</span>{' '}
                    <span className="text-muted-foreground">Recommended for journal submission — ensures transparency to editors and reviewers. The text is appended at the end of the document and below the author's name.</span>
                  </span>
                </label>
              </div>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-serif">Bibliography <Badge variant="outline" className="ml-2 text-xs">C</Badge></CardTitle>
                <CardDescription>
                  Extracts all unique citations across sections and formats them in your chosen academic style.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <ExportCard icon={<FileText className="w-7 h-7" />} title="Vancouver"
                  desc="Standard for medical journals"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'vancouver')}
                  testid="biblio-vancouver" />
                <ExportCard icon={<FileText className="w-7 h-7" />} title="APA 7th"
                  desc="Psychology / social sciences"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'apa')}
                  testid="biblio-apa" />
                <ExportCard icon={<FileType className="w-7 h-7" />} title="BibTeX"
                  desc="For LaTeX users"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'bibtex')}
                  testid="biblio-bibtex" />
                <ExportCard icon={<FileType className="w-7 h-7" />} title="Manubot YAML"
                  desc="citations.yaml override"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'manubot')}
                  testid="biblio-manubot" />
              </CardContent>
            </Card>

            <Card data-testid="multi-style-citations-card">
              <CardHeader>
                <CardTitle className="text-base font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                  Reference list in academic styles
                </CardTitle>
                <CardDescription>
                  Build a complete bibliography from your sections' citations in the journal's preferred style.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  {['apa7', 'vancouver', 'ama', 'bibtex'].map((s) => (
                    <Button key={s} size="sm"
                      variant={citationStyle === s ? 'default' : 'outline'}
                      onClick={() => handleBuildBibliography(s)}
                      disabled={bibLoading}
                      className={citationStyle === s ? 'bg-[#0b1a35] hover:bg-[#1d4ed8] text-white' : ''}
                      data-testid={`biblio-style-${s}`}
                    >
                      {bibLoading && citationStyle === s ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                      {s === 'apa7' ? 'APA 7' : s === 'vancouver' ? 'Vancouver' : s === 'ama' ? 'AMA 11' : 'BibTeX'}
                    </Button>
                  ))}
                  {bibPreview?.bibliography && (
                    <Button size="sm" variant="ghost" onClick={downloadBibliography} className="ml-auto" data-testid="biblio-download-btn">
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="journal-style-picker">
                  <Input className="w-56 h-8 text-xs" value={cslQuery}
                    placeholder="Journal style: lancet, nature, jama…"
                    onChange={async (e) => {
                      const q = e.target.value;
                      setCslQuery(q);
                      if (q.trim().length < 2) { setCslStyles([]); return; }
                      try {
                        const res = await thesisApi.citationStyles(user.id, q.trim());
                        setCslStyles(res.styles || []);
                      } catch { setCslStyles([]); }
                    }}
                    data-testid="csl-style-input" />
                  {cslStyles.slice(0, 6).map((s) => (
                    <Button key={s.id} size="sm" variant={citationStyle === s.id ? 'default' : 'outline'}
                      className="h-8 text-[11px]"
                      onClick={() => handleBuildBibliography(s.id)} disabled={bibLoading}
                      data-testid={`csl-style-${s.id}`}>
                      {s.title}
                    </Button>
                  ))}
                  {cslQuery.trim().length >= 2 && !cslStyles.length && (
                    <span className="text-[11px] text-muted-foreground">no matching style</span>
                  )}
                  <span className="text-[11px] text-muted-foreground">10 000+ journal styles (CSL)</span>
                </div>
                {bibPreview && (
                  <pre className="mt-3 p-3 rounded-lg bg-muted/40 dark:bg-white/[0.02] text-[12px] leading-relaxed overflow-auto max-h-72 whitespace-pre-wrap font-mono" data-testid="biblio-preview">
                    {bibPreview.bibliography || '— No citations found in your sections yet. —'}
                  </pre>
                )}
                {bibPreview && (
                  <p className="text-[11px] text-muted-foreground">{bibPreview.count} references resolved.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">About the Manubot format</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>
                  The export uses the official <a className="underline" href="https://manubot.org" target="_blank" rel="noopener noreferrer">Manubot</a> citation syntax:
                  <code className="ml-1 px-1 rounded bg-muted">@doi:10.xxxx/yy</code> and
                  <code className="ml-1 px-1 rounded bg-muted">@pmid:12345</code>.
                </p>
                <p>
                  For a full Manubot build (PDF, web site with bibliography) clone the Manubot template, paste the exported .md, and start <code className="px-1 rounded bg-muted">manubot process</code>.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          </div>
        </Tabs>

        {/* AI Reviewer Dialog (kept as legacy fallback; inline rendering is now in IntelligencePanel Reviewer tab) */}
        <Dialog open={false} onOpenChange={() => {}}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-serif">
                <Sparkles className="w-5 h-5 text-amber-600" />
                AI Academic Review
              </DialogTitle>
              <DialogDescription>
                A simulated peer reviewer reads your section and gives concrete recommendations.
              </DialogDescription>
            </DialogHeader>
            {reviewLoading || !review ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                <p className="text-sm text-muted-foreground">
                  The reviewer is analysing the section (30-60s)...
                </p>
              </div>
            ) : (
              <div className="space-y-5" data-testid="review-results">
                {/* Score */}
                <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-amber-50 to-teal-50 dark:from-amber-950/30 dark:to-teal-950/30 border">
                  <div className={`text-5xl font-bold ${
                    review.overall_score >= 8 ? 'text-green-600 dark:text-green-400' :
                    review.overall_score >= 6 ? 'text-amber-600 dark:text-amber-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>{review.overall_score}<span className="text-2xl text-muted-foreground">/10</span></div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Overall score</div>
                    <p className="text-sm leading-relaxed">{review.summary}</p>
                  </div>
                </div>

                {/* Strengths */}
                {review.strengths?.length > 0 && (
                  <div data-testid="review-strengths">
                    <h4 className="font-semibold text-sm mb-2 text-green-700 dark:text-green-400">
                      ✓ Strengths
                    </h4>
                    <ul className="space-y-1 text-sm">
                      {review.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-1">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Issues */}
                {review.issues?.length > 0 && (
                  <div data-testid="review-issues">
                    <h4 className="font-semibold text-sm mb-2">⚠ Issues & suggestions ({review.issues.length})</h4>
                    <div className="space-y-3">
                      {review.issues.map((iss, i) => {
                        const sev = iss.severity || 'medium';
                        const sevClass = sev === 'high'
                          ? 'border-l-red-500 bg-red-50 dark:bg-red-950/30'
                          : sev === 'medium'
                          ? 'border-l-amber-500 bg-amber-50 dark:bg-amber-950/30'
                          : 'border-l-blue-400 bg-blue-50 dark:bg-blue-950/30';
                        const sevLabel = sev === 'high' ? 'Critical' : sev === 'medium' ? 'Medium' : 'Minor';
                        const isApplying = applyingIssue === i;
                        const isApplied = iss.applied;
                        return (
                          <div key={i} className={`border-l-4 p-3 rounded-r ${sevClass}`} data-testid={`review-issue-${i}`}>
                            <div className="flex items-center justify-between mb-1 gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Badge variant="outline" className="text-xs shrink-0">{sevLabel}</Badge>
                                {iss.where && <span className="text-xs font-mono text-muted-foreground truncate">{iss.where}</span>}
                              </div>
                              {isApplied ? (
                                <Badge variant="default" className="bg-green-600 text-white shrink-0" data-testid={`issue-applied-${i}`}>
                                  ✓ Applied
                                </Badge>
                              ) : (
                                <Button size="sm" variant="default"
                                  className="shrink-0 bg-teal-700 hover:bg-teal-800 text-white text-xs h-7"
                                  onClick={() => handleApplySuggestion(i)}
                                  disabled={isApplying || applyingIssue !== null}
                                  data-testid={`apply-suggestion-${i}`}>
                                  {isApplying
                                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Applying...</>
                                    : <><Sparkles className="w-3 h-3 mr-1" />Auto-fix</>}
                                </Button>
                              )}
                            </div>
                            <p className="text-sm font-medium mb-1">{iss.comment}</p>
                            {iss.suggestion && (
                              <p className="text-xs text-muted-foreground italic">
                                <span className="font-semibold">Suggestion:</span> {iss.suggestion}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Version History Dialog (feature H) */}
        <Dialog open={!!versionsOpenFor} onOpenChange={(o) => { if (!o) setVersionsOpenFor(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit history</DialogTitle>
              <DialogDescription>
                Snapshots saved before each edit. You can restore any version.
              </DialogDescription>
            </DialogHeader>
            {versionsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : versions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No versions saved yet. The first snapshot will be created on next edit.
              </p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="versions-list">
                {versions.map((v) => (
                  <Card key={v.id} data-testid={`version-${v.id}`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-muted-foreground">
                            {new Date(v.created_at).toLocaleString('bg-BG')} · {v.note}
                          </div>
                          <div className="text-sm font-medium mt-1">{v.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {v.markdown?.slice(0, 200)}...
                          </div>
                        </div>
                        <Button size="sm" variant="outline"
                          onClick={() => handleRestoreVersion(versionsOpenFor, v.id)}
                          data-testid={`restore-${v.id}`}>
                          Restore
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* EQUATOR Compliance + Evidence dialogs */}
        {complianceChapter && (
          <ComplianceCheckDialog
            open={complianceOpen}
            onOpenChange={(o) => { setComplianceOpen(o); if (!o) setComplianceChapter(null); }}
            userId={user.id}
            chapterId={complianceChapter.id}
            chapterTitle={complianceChapter.title}
            onChapterUpdated={loadAll}
          />
        )}
        <EvidenceDialog
          open={evidenceOpen}
          onOpenChange={setEvidenceOpen}
          userId={user.id}
          projectId={projectId}
          initialQuery={evidenceQuery}
        />

        {/* Claim Safety pre-generation warning */}
        <ClaimSafetyDialog
          open={safetyDialogOpen}
          result={safetyResult}
          mode="pre-generation"
          onCancel={handleSafetyCancel}
          onContinue={handleSafetyContinue}
        />

        {/* Findings Table extractor */}
        <FindingsTableDialog
          open={findingsTableOpen}
          onOpenChange={setFindingsTableOpen}
          userId={user.id}
          projectId={projectId}
          language={project?.language || 'en'}
        />

        {/* Premium per-section writer — Researcher plan and above */}
        <PremiumUpgradeDialog
          open={premiumUpgradeOpen}
          onOpenChange={setPremiumUpgradeOpen}
          premium={featureFlags.premium}
        />
        {featureFlags.premium_section_writer && (
          <PremiumSectionDialog
            open={premiumOpen}
            onOpenChange={setPremiumOpen}
            userId={user.id}
            projectId={projectId}
            language={aiLanguage}
            onGenerated={() => { loadAll(); }}
          />
        )}

        {/* Plagiarism / originality check */}
        <PlagiarismDialog
          open={plagiarismOpen}
          onOpenChange={setPlagiarismOpen}
          userId={user.id}
          projectId={projectId}
          chapterId={openChapter?.id}
        />

        {/* Sprint 1 — AI Structure Builder (dissertation chapter architecture) */}
        <AIStructureBuilderDialog
          open={structureBuilderOpen}
          onOpenChange={setStructureBuilderOpen}
          userId={user.id}
          project={project}
          onApplied={() => { loadAll(); setTab('chapters'); }}
        />

        {/* Sprint 1 — Chapter-specific Library (attach docs to a single chapter) */}
        {chapterLibraryFor && (
          <ChapterLibraryDialog
            open={!!chapterLibraryFor}
            onOpenChange={(v) => !v && setChapterLibraryFor(null)}
            userId={user.id}
            projectId={projectId}
            chapter={chapterLibraryFor}
            allDocuments={docs}
            onUpdated={() => { loadAll(); }}
          />
        )}

        {/* Sprint 3 — Defense Simulation (5 AI reviewers) + Sprint 7 inline */}
        {openChapter && (
          <DefenseSimulationDialog
            open={defenseOpen}
            onOpenChange={setDefenseOpen}
            userId={user.id}
            chapter={openChapter}
            language={aiLanguage}
            onChapterPatched={(newMd) => patchChapterMarkdown(openChapter.id, newMd)}
          />
        )}

        {/* Wave D — Statistical / Gap / Novelty analyzers */}
        {openChapter && (
          <WaveDDialog
            open={waveDOpen}
            onOpenChange={setWaveDOpen}
            userId={user.id}
            projectId={projectId}
            chapter={openChapter}
            language={aiLanguage}
          />
        )}

        {/* Pre-publish Readiness Score — orchestrates all checkers into 1 score */}
        {openChapter && (
          <ReadinessDashboardDialog
            open={readinessOpen}
            onOpenChange={setReadinessOpen}
            userId={user.id}
            projectId={projectId}
            chapter={openChapter}
            language={aiLanguage}
          />
        )}

        {/* Project-wide aggregated Readiness Overview */}
        <ProjectReadinessOverviewDialog
          open={projectReadinessOpen}
          onOpenChange={setProjectReadinessOpen}
          userId={user.id}
          projectId={projectId}
          onOpenChapter={(chapterId) => setOpenChapterId(chapterId)}
        />

        {/* Sprint 7 — Smart Continue (paragraph-anchored or cursor-anchored) */}
        {openChapter && (
          <SmartContinueDialog
            open={smartContinueState.open}
            onOpenChange={(o) => setSmartContinueState((s) => ({ ...s, open: o }))}
            userId={user.id}
            projectId={projectId}
            language={aiLanguage}
            precedingText={smartContinueState.precedingText}
            followingText={smartContinueState.followingText}
            anchorLabel={smartContinueState.anchorLabel}
            onInsert={async (continuation) => {
              const md = openChapter.markdown || '';
              const before = smartContinueState.precedingText;
              const after = smartContinueState.followingText;
              if (!continuation || !continuation.trim()) return;
              // Splice: before + space + continuation + after
              const trimmedBefore = before.trimEnd();
              const endsWithStop = /[.!?…»")\]]$/.test(trimmedBefore);
              const sep = endsWithStop ? ' ' : '';
              const newMd = `${trimmedBefore}${sep}${continuation.trim()}${after ? '\n\n' + after.trimStart() : '\n'}`;
              try {
                await thesisApi.updateChapter(user.id, openChapter.id, { markdown: newMd });
                patchChapterMarkdown(openChapter.id, newMd);
                toast.success(`Added ${continuation.trim().split(/\s+/).length} words`);
              } catch (e) {
                toast.error('Insert failed: ' + (e?.response?.data?.detail || e.message));
              }
            }}
          />
        )}

        {/* Sprint 4 — Claim Grounding (PaperQA2 style) + Self-Feedback (OpenScholar) */}
        {openChapter && (
          <ClaimGroundingDialog
            open={groundingOpen}
            onOpenChange={setGroundingOpen}
            userId={user.id}
            chapter={openChapter}
            language={aiLanguage}
            onRequestSelfFeedback={() => loadAll()}
          />
        )}

        {/* Selection toolbar — appears on any text selection inside a chapter content scope */}
        <SelectionToolbar
          userId={user.id}
          projectId={projectId}
          language={project?.language || 'en'}
          onReplace={handleSelectionReplace}
        />
      </main>
      <AppFooter lang="en" />
    </div>
  );
}

// ----------------------------------------------------------------------
// helpers
// ----------------------------------------------------------------------

function renderCitations(children, verification) {
  // walk react children, replace @doi:/@pmid:/@tag: tokens inside strings with links
  if (typeof children === 'string') return tokenize(children, verification);
  if (Array.isArray(children)) return children.map((c, i) =>
    typeof c === 'string' ? <span key={i}>{tokenize(c, verification)}</span> : c
  );
  return children;
}

function _verificationStatus(verification, token) {
  if (!verification?.citations) return null;
  const match = verification.citations.find((c) => c.token === token);
  return match?.status || null;
}

function _statusClass(status) {
  if (status === 'verified') return 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/40 border-green-300 dark:border-green-800';
  if (status === 'unverified') return 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800';
  return 'text-teal-700 dark:text-teal-400';
}

function tokenize(text, verification) {
  const parts = [];
  const re = /(@doi:[^\s\]\),;]+|@pmid:[0-9]+|@tag:[^\s\]\),;]+)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[1];
    const status = _verificationStatus(verification, tok);
    const className = `font-mono text-[0.78em] px-1 py-0.5 rounded border ${_statusClass(status)}`;
    if (tok.startsWith('@doi:')) {
      const id = tok.slice(5);
      parts.push(
        <a key={i++} href={`https://doi.org/${id}`} target="_blank" rel="noopener noreferrer"
          className={className} title={status ? `Verification: ${status}` : ''}>
          {status === 'unverified' && '⚠ '}DOI:{id.slice(0, 20)}{id.length > 20 ? '…' : ''}
        </a>
      );
    } else if (tok.startsWith('@pmid:')) {
      const id = tok.slice(6);
      parts.push(
        <a key={i++} href={`https://pubmed.ncbi.nlm.nih.gov/${id}/`} target="_blank" rel="noopener noreferrer"
          className={className} title={status ? `Verification: ${status}` : ''}>
          {status === 'unverified' && '⚠ '}PMID:{id}
        </a>
      );
    } else {
      parts.push(<em key={i++} className={className.replace('text-teal-700', 'text-muted-foreground')}>[{tok.slice(5)}]</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function ExportCard({ icon, title, desc, href, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}>
      <Card className="hover:shadow-md transition cursor-pointer h-full">
        <CardContent className="pt-6 flex flex-col items-start gap-2">
          <div className="text-primary">{icon}</div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </CardContent>
      </Card>
    </a>
  );
}


function DashStat({ label, value, icon: Icon }) {
  return (
    <div className="p-4 rounded-lg border border-border bg-muted/30 dark:bg-white/[0.02]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="font-serif text-3xl mt-2 text-[#0b1a35] dark:text-white" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
        {value}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{k}</span>
      <span className="text-sm font-medium">{v}</span>
    </div>
  );
}

function FieldGenCard({ userId, projectId, field, language, label }) {
  const [val, setVal] = useState('');
  return (
    <div className="p-3 rounded-lg border border-border bg-muted/30 dark:bg-white/[0.02]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
        <GenerateFieldButton
          userId={userId} projectId={projectId} field={field} language={language}
          onResult={(v) => setVal(v)}
        />
      </div>
      {val ? (
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{val}</div>
      ) : (
        <div className="text-xs text-muted-foreground italic">Click Generate to draft from your project context + library.</div>
      )}
    </div>
  );
}
